// Stellarium Web - Copyright (c) 2022 - Stellarium Labs SRL
//
// This program is licensed under the terms of the GNU AGPL v3, or
// alternatively under a commercial licence.
//
// The terms of the AGPL v3 license can be found in the main directory of this
// repository.

<template>
  <!-- [guanxing patch] M-SW12：信息卡升级为与太阳系面板同构的左上毛玻璃面板
       （.obj-info-card，token 驱动），替代原灰底 inline 样式。
       [guanxing patch] M-SW52：折叠改全端生效（M-SW25 原仅移动端）——
       所有端默认折叠，只留图标+名称+类型，点箭头展开。 -->
  <v-card v-if="selectedObject" class="obj-info-card" :class="{ 'obj-collapsed': !expanded }">
    <v-btn icon style="position: absolute; right: 0" v-on:click.native="unselect()"><v-icon>mdi-close</v-icon></v-btn>
    <v-card-title primary-title>
      <div style="width: 100%">
        <img class="obj-icon" :src="icon" height="48" width="48" align="left" style="margin-top: 3px; margin-right: 10px"/>
        <div style="overflow: hidden; text-overflow: ellipsis;">
          <div class="text-h5">{{ title }}</div>
          <div class="grey--text text-body-2">{{ type }}</div>
        </div>
      </div>
    </v-card-title>
    <!-- [guanxing patch] M-SW25：移动端默认折叠——仅留图标+名称+类型，
         别名/属性行/介绍文字/古画收进 .obj-more，由底部箭头展开；桌面端恒显示。 -->
    <div class="obj-more" v-show="showMore">
    <v-card-text style="padding-bottom: 5px;">
      <v-row v-if="otherNames.length > 1" style="width: 100%;">
        <v-col cols="12">
          <span style="position: absolute;">{{ $t('Also known as') }}</span><span style="padding-left: 33.3333%">&nbsp;</span><span class="text-caption white--text" v-for="mname in otherNames1to7" :key="mname" style="margin-right: 15px; font-weight: 500;">{{ mname }}</span>
          <v-btn small icon class="grey--text" v-if="otherNames.length > 8" v-on:click.native="showMinorNames = !showMinorNames" style="margin-top: -5px; margin-bottom: -5px;"><v-icon>mdi-dots-horizontal</v-icon></v-btn>
          <span class="text-caption white--text" v-for="mname in otherNames8andMore" :key="mname" style="margin-right: 15px; font-weight: 500">{{ mname }}</span>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-text>
      <template v-for="item in items">
        <v-row style="width: 100%" :key="item.key" no-gutters>
          <v-col cols="4" style="color: #dddddd">{{ item.key }}</v-col>
          <v-col cols="8" style="font-weight: 500" class="white--text"><span v-html="item.value"></span></v-col>
        </v-row>
      </template>
      <!-- [guanxing patch] M-SW9：本地知识块（中外星官/星座 lore，零外网）。
           标题次级强调色（--gx-gold，M-SW24 单色化后为浅灰白），
           正文 pre-line 保留分段；M-SW12 样式收敛到 style 块。 -->
      <div v-if="loreBlocks.length" class="lore-blocks white--text">
        <div v-for="(blk, i) in loreBlocks" :key="i" class="lore-block">
          <div class="lore-title">{{ blk.title }}</div>
          <div class="text-body-2 lore-text">{{ blk.text }}</div>
          <!-- [guanxing patch] M-SW11：二十八宿逐宿古画（丁观鹏《五星二十八宿神形图册》，
               CC BY），随 lore 块展示，本地静态文件零外网 -->
          <img v-if="blk.image" :src="blk.image" loading="lazy" :alt="blk.title" class="lore-img"/>
          <div v-if="blk.imageCredit" class="text-caption lore-credit">{{ blk.imageCredit }}</div>
        </div>
      </div>
    </v-card-text>
    </div>
    <v-card-actions style="margin-top: -25px">
      <v-spacer/>
      <template v-for="item in pluginsSelectedInfoExtraGuiComponents">
        <component :is="item" :key="item"></component>
      </template>
    </v-card-actions>
    <!-- [guanxing patch] M-SW25：移动端底部展开/收起箭头（仅移动端显示）。
         [guanxing patch] M-SW52：箭头改全端显示（桌面同样默认折叠、可展开）。 -->
    <div class="obj-toggle" @click.stop="expanded = !expanded">
      <v-icon small>{{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
    </div>
    <!-- [guanxing patch] M-SW18：信息卡分享链接按钮及其弹层按用户需求移除
         （链接生成函数一并删除，见 sw_helpers.js 对应注释）。 -->
    <div v-if="$store.state.showSelectedInfoButtons" style="position: absolute; right: 0px; bottom: -50px;">
      <v-btn v-if="showPointToButton" fab small color="transparent" v-on:click.native="lockToSelection()">
        <img src="@/assets/images/svg/ui/point_to.svg" height="40px" style="min-height: 40px"></img>
      </v-btn>
      <v-btn v-if="!showPointToButton" fab small color="transparent" @mousedown="zoomOutButtonClicked()">
        <img :class="{bt_disabled: !zoomOutButtonEnabled}" src="@/assets/images/svg/ui/remove_circle_outline.svg" height="40px" style="min-height: 40px"></img>
      </v-btn>
      <v-btn v-if="!showPointToButton" fab small color="transparent" @mousedown="zoomInButtonClicked()">
        <img :class="{bt_disabled: !zoomInButtonEnabled}" src="@/assets/images/svg/ui/add_circle_outline.svg" height="40px" style="min-height: 40px"></img>
      </v-btn>
    </div>
  </v-card>
</template>

<script>

import Moment from 'moment'
import swh from '@/assets/sw_helpers.js'
// [guanxing patch] M-SW33 曾引入 PHONE_MEDIA 做手机断点判定（isMobile）；
// M-SW52 折叠改全端后该判定与监听整体删除（不留死代码）。
// [guanxing patch] M-SW53：星命上下文数据源（星次/星君独属介绍文案）。
import { XINGCI, LUNAR_MANSIONS } from '@/assets/birth-fate.js'

export default {
  data: function () {
    return {
      showMinorNames: false,
      // [guanxing patch] M-SW25：折叠态（默认折叠，点箭头展开）。
      // [guanxing patch] M-SW52：折叠改全端默认；锁定期内展开态恒保持
      // （切换锁定对象不复位），点背景天空折叠（见 stelSelectionId watcher）。
      expanded: false,
      // [guanxing patch] M-SW9：本地知识块（[{title,text}]，loreBlocksForSkySource 产出）
      loreBlocks: [],
      items: []
    }
  },
  computed: {
    // [guanxing patch] M-SW25→M-SW52：折叠改全端——只看展开态，
    // 所有端默认折叠（图标+名称+类型），展开后显示别名/属性行/介绍/古画。
    showMore: function () {
      return this.expanded
    },
    selectedObject: function () {
      return this.$store.state.selectedObject
    },
    // [guanxing patch] M-SW53：星命弹窗跳转上下文（{type:'xingci'|'lord',
    // name, objId}）。命中且选中对象匹配时，知识栏内容按上下文替换/并列
    // （星次 → 只显示星次块；星君 → 星君块 + 宿块并列）。
    fateView: function () {
      return this.$store.state.fateView
    },
    stelSelectionId: function () {
      return this.$store.state.stel && this.$store.state.stel.selection ? this.$store.state.stel.selection : undefined
    },
    title: function () {
      // [guanxing patch] M-SW53：星次上下文标题改显星次名（用户定案「只显示
      // 星次介绍」——头部不再顶着对应星座名）；星君/宿上下文标题维持宿名。
      if (this.fateView && this.fateView.type === 'xingci' && this.selectedObject) {
        return '星次 · ' + this.fateView.name
      }
      return this.selectedObject ? this.otherNames[0] : 'Selection'
    },
    otherNames: function () {
      return this.selectedObject ? swh.namesForSkySource(this.selectedObject, 26) : undefined
    },
    otherNames1to7: function () {
      return this.otherNames.slice(1, 8)
    },
    otherNames8andMore: function () {
      return this.showMinorNames ? this.otherNames.slice(8) : []
    },
    type: function () {
      if (!this.selectedObject) return this.$t('Unknown')
      // [guanxing patch] M-QZ：四余虚星固定类型名；同时规避合成 SkySource
      // types 为空表时下方 types[0] 通路出 undefined。
      if (swh.qzsyDefForSkySource(this.selectedObject)) return '七政四余 · 虚星'
      // [guanxing patch] M-SW53：星命上下文类型标记（归属感：这是「你的星次/
      // 你的星君」），宿/星座本身的类型名让位。
      if (this.fateView && this.fateView.type === 'xingci' && this.selectedObject) return '中国十二星次'
      if (this.fateView && this.fateView.type === 'lord' && this.selectedObject) return '二十八宿星君'
      let morpho = ''
      if (this.selectedObject.model_data && this.selectedObject.model_data.morpho) {
        morpho = swh.nameForGalaxyMorpho(this.selectedObject.model_data.morpho)
        if (morpho) {
          morpho = morpho + ' '
        }
      }
      return morpho + swh.nameForSkySourceType(this.selectedObject.types[0])
    },
    icon: function () {
      return swh.iconForSkySource(this.selectedObject)
    },
    showPointToButton: function () {
      if (!this.$store.state.stel.lock) return true
      if (this.$store.state.stel.lock !== this.$store.state.stel.selection) return true
      return false
    },
    zoomInButtonEnabled: function () {
      if (!this.$store.state.stel.lock || !this.selectedObject) return false
      return true
    },
    zoomOutButtonEnabled: function () {
      if (!this.$store.state.stel.lock || !this.selectedObject) return false
      return true
    },
    extraButtons: function () {
      return swh.selectedObjectExtraButtons
    },
    pluginsSelectedInfoExtraGuiComponents: function () {
      let res = []
      for (const i in this.$stellariumWebPlugins()) {
        const plugin = this.$stellariumWebPlugins()[i]
        if (plugin.selectedInfoExtraGuiComponents) {
          res = res.concat(plugin.selectedInfoExtraGuiComponents)
        }
      }
      return res
    }
  },
  watch: {
    selectedObject: function (s) {
      this.showMinorNames = false
      // [guanxing patch] M-SW25：切换天体时折叠态复位，新选中默认只显示精简信息。
      // [guanxing patch] M-SW52：锁定期内展开态恒保持（切换锁定对象不复位，
      // 用户定案「锁定期内恒保持」）；未锁定的普通选中维持「新选中默认折叠」。
      if (!this.$store.state.stel.lock) this.expanded = false
      if (!s) {
        this.loreBlocks = []
        if (this.timer) clearInterval(this.timer)
        this.timer = undefined
        return
      }
      var that = this
      that.items = that.computeItems()
      if (that.timer) clearInterval(that.timer)
      that.timer = setInterval(() => { that.items = that.computeItems() }, 1000)

      // [guanxing patch] M-SW53：星命上下文校验——选中对象的名表不含上下文
      // objId（用户点了别的天体/星座）→ 清除上下文，知识栏回到通用内容。
      // 校验在本 watcher 同步清除，早于本组件渲染刷新，标题不会闪旧值。
      const fv = this.$store.state.fateView
      if (fv && !(s.names && s.names.indexOf(fv.objId) >= 0)) {
        this.$store.commit('setValue', { varName: 'fateView', newValue: null })
      }

      // [guanxing patch] M-SW9：本地知识块随选中异步装载；切换选中时以
      // selection id 比对丢弃过期结果（防止慢返回覆盖新选中）。
      const selId = this.stelSelectionId
      const fvNow = this.$store.state.fateView
      if (fvNow && fvNow.type === 'xingci') {
        // [guanxing patch] M-SW53（用户定案）：星次行跳转 → 知识栏只显示
        // 星次介绍，不显示对应星座介绍（连线仍是该星座金黄连线）。
        that.loreBlocks = this.fateXingciBlocks(fvNow)
      } else {
        swh.loreBlocksForSkySource(s, selId).then(blocks => {
          if (this.stelSelectionId === selId) {
            // [guanxing patch] M-SW53（用户定案）：星君行跳转 → 星君块并列
            // 在宿块之前（宿块含古画与「星宿与日期」注记，照常产出）。
            const fv2 = this.$store.state.fateView
            that.loreBlocks = (fv2 && fv2.type === 'lord')
              ? this.fateLordBlocks(fv2).concat(blocks)
              : blocks
          }
        }, () => { that.loreBlocks = [] })
      }
    },
    stelSelectionId: function (s) {
      if (!this.$stel.core.selection) {
        // [guanxing patch] M-SW52：锁定期常驻 + 点背景天空折叠（用户定案）。
        // 引擎 on_click（vendor movements.c）点天空空白/未命中任何天体时把
        // selection 清空而 lock 保留——此处把选中恢复为锁定对象：知识栏不
        // 消失（常驻）；同时折叠展开态（点天空 = 收起内容，不关卡片）。
        // ✕ 关闭路径先清 lock 再清 selection，不会进入本分支。
        const lockObj = this.$stel.core.lock
        if (lockObj) {
          this.expanded = false
          this.$stel.core.selection = lockObj
          return
        }
        this.$store.commit('setSelectedObject', 0)
        return
      }
      swh.sweObj2SkySource(this.$stel.core.selection).then(res => {
        this.$store.commit('setSelectedObject', res)
      }, err => {
        console.log("Couldn't find info for object " + s + ':' + err)
        this.$store.commit('setSelectedObject', 0)
      })
    }
  },
  methods: {
    // [guanxing patch] M-SW53：星次上下文知识块（birth-fate.js XINGCI 文案，
    // 纯本地零外网）。命中失败（数据缺字段）返回空数组——知识块是增强信息，
    // 不允许打断信息卡。
    fateXingciBlocks: function (fv) {
      const e = XINGCI.find((x) => x.name === fv.name)
      if (!e || !e.lore) return []
      return [{
        title: '星次 · ' + e.name,
        text: (e.sum || '') + '\n\n太阳行经：' + (e.qi || '') + '\n\n' + e.lore + '\n\n对应西方占星宫：' + e.sign
      }]
    },
    // [guanxing patch] M-SW53：星君上下文知识块（并列于宿块之前）。
    fateLordBlocks: function (fv) {
      const m = LUNAR_MANSIONS.find((x) => x.lord === fv.name)
      if (!m || !m.lordLore) return []
      return [{
        title: '本命星君 · ' + m.lord,
        text: (m.lordSum || '') + '\n\n' + m.lordLore
      }]
    },
    computeItems: function () {
      const obj = this.$stel.core.selection
      if (!obj) return []
      const that = this

      // [guanxing patch] M-QZ：四余虚星信息卡——显示实时黄道经度与天文
      // 含义，不走 vmag/distance 通用路径（circle 无此类属性）。条目随
      // 1s 定时刷新，时间流动时经度实时更新。
      const qzDef = swh.qzsyDefForSkySource(this.selectedObject)
      if (qzDef) {
        // 跟随引擎时间（observer.tt，MJD）：显示的是屏上星空时刻的黄道经度。
        const lon = swh.qzsyLon(this.$stel.core.observer.tt + 2400000.5)[qzDef.key]
        return [
          { key: '黄道经度', value: lon.toFixed(2) + '°' },
          { key: '含义', value: qzDef.meaning }
        ]
      }

      const ret = []

      const addAttr = (key, attr, format) => {
        const v = obj.getInfo(attr)
        if (v && !isNaN(v)) {
          ret.push({
            key: key,
            value: format ? format(v) : v.toString()
          })
        }
      }

      addAttr(that.$t('Magnitude'), 'vmag', this.formatMagnitude)
      addAttr(that.$t('Distance'), 'distance', this.formatDistance)
      if (this.selectedObject.model_data) {
        if (this.selectedObject.model_data.radius) {
          ret.push({
            key: that.$t('Radius'),
            value: this.selectedObject.model_data.radius.toString() + ' Km'
          })
        }
        if (this.selectedObject.model_data.spect_t) {
          ret.push({
            key: that.$t('Spectral Type'),
            value: this.selectedObject.model_data.spect_t
          })
        }
        if (this.selectedObject.model_data.dimx) {
          const dimy = this.selectedObject.model_data.dimy ? this.selectedObject.model_data.dimy : this.selectedObject.model_data.dimx
          ret.push({
            key: that.$t('Size'),
            value: this.selectedObject.model_data.dimx.toString() + "' x " + dimy.toString() + "'"
          })
        }
      }
      const formatInt = function (num, padLen) {
        const pad = new Array(1 + padLen).join('0')
        return (pad + num).slice(-pad.length)
      }
      const formatRA = function (a) {
        const raf = that.$stel.a2tf(a, 1)
        return '<div class="radecVal">' + formatInt(raf.hours, 2) + '<span class="radecUnit">h</span>&nbsp;</div><div class="radecVal">' + formatInt(raf.minutes, 2) + '<span class="radecUnit">m</span></div><div class="radecVal">' + formatInt(raf.seconds, 2) + '.' + raf.fraction + '<span class="radecUnit">s</span></div>'
      }
      const formatAz = function (a) {
        const raf = that.$stel.a2af(a, 1)
        return '<div class="radecVal">' + formatInt(raf.degrees < 0 ? raf.degrees + 180 : raf.degrees, 3) + '<span class="radecUnit">°</span></div><div class="radecVal">' + formatInt(raf.arcminutes, 2) + '<span class="radecUnit">\'</span></div><div class="radecVal">' + formatInt(raf.arcseconds, 2) + '.' + raf.fraction + '<span class="radecUnit">"</span></div>'
      }
      const formatDec = function (a) {
        const raf = that.$stel.a2af(a, 1)
        return '<div class="radecVal">' + raf.sign + formatInt(raf.degrees, 2) + '<span class="radecUnit">°</span></div><div class="radecVal">' + formatInt(raf.arcminutes, 2) + '<span class="radecUnit">\'</span></div><div class="radecVal">' + formatInt(raf.arcseconds, 2) + '.' + raf.fraction + '<span class="radecUnit">"</span></div>'
      }
      const posCIRS = this.$stel.convertFrame(this.$stel.core.observer, 'ICRF', 'JNOW', obj.getInfo('radec'))
      const radecCIRS = this.$stel.c2s(posCIRS)
      const raCIRS = this.$stel.anp(radecCIRS[0])
      const decCIRS = this.$stel.anpm(radecCIRS[1])
      ret.push({
        key: that.$t('Ra/Dec'),
        value: formatRA(raCIRS) + '&nbsp;&nbsp;&nbsp;' + formatDec(decCIRS)
      })
      const azalt = this.$stel.c2s(this.$stel.convertFrame(this.$stel.core.observer, 'ICRF', 'OBSERVED', obj.getInfo('radec')))
      const az = this.$stel.anp(azalt[0])
      const alt = this.$stel.anpm(azalt[1])
      ret.push({
        key: that.$t('Az/Alt'),
        value: formatAz(az) + '&nbsp;&nbsp;&nbsp;' + formatDec(alt)
      })
      addAttr(that.$t('Phase'), 'phase', this.formatPhase)
      const vis = obj.computeVisibility()
      let str = ''
      if (vis.length === 0) {
        str = that.$t('Not visible tonight')
      } else if (vis[0].rise === null) {
        str = that.$t('Always visible tonight')
      } else {
        str = that.$t('Rise: {0}&nbsp;&nbsp;&nbsp; Set: {1}', [this.formatTime(vis[0].rise), this.formatTime(vis[0].set)])
      }
      ret.push({
        key: that.$t('Visibility'),
        value: str
      })
      return ret
    },
    formatPhase: function (v) {
      return (v * 100).toFixed(0) + '%'
    },
    formatMagnitude: function (v) {
      if (!v) {
        return 'Unknown'
      }
      return v.toFixed(2)
    },
    formatDistance: function (d) {
      // d is in AU
      if (!d) {
        return 'NAN'
      }
      const ly = d * swh.astroConstants.ERFA_AULT / swh.astroConstants.ERFA_DAYSEC / swh.astroConstants.ERFA_DJY
      if (ly >= 0.1) {
        return ly.toFixed(2) + '<span class="radecUnit"> light years</span>'
      }
      if (d >= 0.1) {
        return d.toFixed(2) + '<span class="radecUnit"> AU</span>'
      }
      const meter = d * swh.astroConstants.ERFA_DAU
      if (meter >= 1000) {
        return (meter / 1000).toFixed(2) + '<span class="radecUnit"> km</span>'
      }
      return meter.toFixed(2) + '<span class="radecUnit"> m</span>'
    },
    formatTime: function (jdm) {
      var d = new Date()
      d.setMJD(jdm)
      const utc = new Moment(d)
      utc.utcOffset(this.$store.state.stel.utcoffset)
      return utc.format('HH:mm')
    },
    unselect: function () {
      // [guanxing patch] M-SW52：✕ = 关闭知识栏 + 解除锁定（用户定案）。
      // 必须先清 lock 再清 selection，绕开锁定期恢复逻辑（否则卡片会被
      // 立即拉回）；旧实现只清 selection，锁定期下视角锁定会残留。
      // [guanxing patch] M-SW53：同时清星命上下文（✕ 后回到通用内容）。
      this.$store.commit('setValue', { varName: 'fateView', newValue: null })
      this.$stel.core.lock = null
      this.$stel.core.selection = 0
    },
    lockToSelection: function () {
      if (this.$stel.core.selection) {
        this.$stel.pointAndLock(this.$stel.core.selection, 0.5)
      }
    },
    zoomInButtonClicked: function () {
      const currentFov = this.$store.state.stel.fov * 180 / Math.PI
      this.$stel.zoomTo(currentFov * 0.3 * Math.PI / 180, 0.4)
      const that = this
      this.zoomTimeout = setTimeout(_ => { that.zoomInButtonClicked() }, 300)
    },
    zoomOutButtonClicked: function () {
      const currentFov = this.$store.state.stel.fov * 180 / Math.PI
      this.$stel.zoomTo(currentFov * 3 * Math.PI / 180, 0.6)
      const that = this
      this.zoomTimeout = setTimeout(_ => { that.zoomOutButtonClicked() }, 200)
    },
    stopZoom: function () {
      if (this.zoomTimeout) {
        clearTimeout(this.zoomTimeout)
        this.zoomTimeout = undefined
      }
    },
    extraButtonClicked: function (btn) {
      btn.callback()
    }
  },
  mounted: function () {
    const that = this
    window.addEventListener('mouseup', function (event) {
      that.stopZoom()
    })
    // [guanxing patch] M-SW33 曾在此跟踪手机断点（isMobile）；M-SW52 折叠
    // 改全端后判定与监听整体删除（不留死代码）。
  }
}
</script>

<style>
/* [guanxing patch] M-SW12：信息卡面板——与太阳系 #ui 同材质同圆角，
   token 驱动（App.vue :root）；限高内滚动防长内容盖住底栏。M-SW24 单色化。 */
.obj-info-card {
  background: var(--gx-panel-bg)!important;
  border: 1px solid var(--gx-panel-border)!important;
  border-radius: var(--gx-radius-panel)!important;
  box-shadow: var(--gx-shadow);
  color: var(--gx-text);
  max-height: calc(100vh - 130px);
  overflow-y: auto;
}
.obj-info-card::-webkit-scrollbar { width: 8px; }
.obj-info-card::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.25); border-radius: 4px; }

/* [guanxing patch] M-SW9/M-SW11 知识块与古画样式（M-SW12 从 inline 收敛至此）。
   M-SW24：古画灰度化融入单色体系（保留轮廓层次，contrast 微降防细线发闷）。 */
.lore-blocks { margin-top: 15px; }
.lore-block { margin-bottom: 10px; }
.lore-title { font-weight: 600; color: var(--gx-gold); }
.lore-text { white-space: pre-line; line-height: 1.6; color: var(--gx-text-dim); }
.lore-img {
  width: 100%; max-width: 480px; display: block; border-radius: var(--gx-radius-ctrl); margin-top: 8px;
  filter: grayscale(1) contrast(0.92);
  border: 1px solid var(--gx-panel-border);
}
.lore-credit { color: var(--gx-text-dim); margin-top: 4px; }

.bt_disabled {
  filter: opacity(0.2);
}

.radecVal {
  display: inline-block;
  font-family: monospace;
  padding-right: 2px;
  font-size: 13px;
  font-weight: bold;
}

.radecUnit {
  color: #dddddd;
  font-weight: normal
}

/* [guanxing patch] M-SW25：移动端信息卡整体缩小 + 默认折叠（用户需求：
   「移动端弹出的星座/星宿介绍框太大了」）。宽度写在 gui.vue 的 inline style
   （380px），inline 权重更高，故此处覆盖必须 !important；断点沿用项目既有
   移动端约定 600px（见 bottom-bar.vue / birth-overlay.vue）。桌面端严格不变。 */
.obj-toggle {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 24px;
  cursor: pointer;
  color: var(--gx-text-dim);
  border-top: 1px solid var(--gx-panel-border);
}
.obj-toggle:hover { color: var(--gx-text); }

/* [guanxing patch] M-SW52：折叠态改全端生效（原 M-SW25 仅移动端 @media 内）——
   只留标题块（图标+名称+类型），其余内容与插件动作行隐藏。 */
.obj-info-card.obj-collapsed > .obj-more,
.obj-info-card.obj-collapsed > .v-card__actions { display: none !important; }

/* [guanxing patch] M-SW33：手机判定放宽为「短边≤600 且触屏」，手机横屏
   （宽>600px）不再掉回桌面布局。字面量须与 assets/phone-media.js 同步。 */
@media all and (max-width: 600px), (pointer: coarse) and (max-height: 600px) and (max-width: 1100px) {
  /* 略微收窄：380px → 330px */
  .obj-info-card {
    width: 330px !important;
    max-width: calc(100vw - 12px) !important;
  }
  /* 图标与标题等比调小 */
  .obj-info-card .obj-icon { width: 36px !important; height: 36px !important; }
  .obj-info-card .v-card__title { padding: 10px 12px 6px !important; }
  .obj-info-card .text-h5 { font-size: 1.15rem !important; line-height: 1.25 !important; }
  .obj-info-card .v-card__text { padding: 8px 12px !important; font-size: 13px; }
  .obj-info-card .lore-text { font-size: 12.5px; line-height: 1.55; }
  .obj-info-card .radecVal { font-size: 12px; }
}
</style>
